import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
import { Button } from "../design/button";
import { Heading } from "../design/heading";
import { Text } from "../design/text";
import { Textarea } from "../design/textarea";
import { Field, Label } from "../design/fieldset";
import logo from "../assets/logo.png";
import {
  CheckCircleIcon,
  XCircleIcon,
  CalendarDaysIcon,
  ClockIcon,
  MapPinIcon,
} from "@heroicons/react/24/outline";

type ResponseState = "loading" | "ready" | "responding" | "success" | "error" | "not_found";

const InvitationResponsePage: React.FC = () => {
  const { visitId } = useParams<{ visitId: string }>();
  const [searchParams] = useSearchParams();
  const parentId = searchParams.get("parentId");
  const action = searchParams.get("action"); // "accept" or "decline"

  const [state, setState] = useState<ResponseState>("loading");
  const [visitData, setVisitData] = useState<any>(null);
  const [message, setMessage] = useState("");
  const [responseType, setResponseType] = useState<"accepted" | "declined" | "change_requested" | null>(
    action === "accept" ? "accepted" : action === "decline" ? "declined" : null
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!visitId) {
      setState("not_found");
      return;
    }

    const fetchVisit = async () => {
      try {
        const fn = httpsCallable(functions, "getVisitInvitation");
        const result = await fn({ visitId, parentId });
        const data = result.data as any;
        setVisitData(data);

        // Check if already responded
        const invStatus = data.invitation?.status;
        if (invStatus === "accepted" || invStatus === "confirmed") {
          setResponseType("accepted");
          setState("success");
          return;
        }
        if (invStatus === "declined" || invStatus === "denied") {
          setResponseType("declined");
          setState("success");
          return;
        }
        if (invStatus === "change_requested") {
          setResponseType("change_requested");
          setState("success");
          return;
        }

        setState("ready");
      } catch (err) {
        console.error("Error loading visit:", err);
        setState("not_found");
      }
    };

    fetchVisit();
  }, [visitId, parentId]);

  const handleRespond = async (response: "accepted" | "declined" | "change_requested") => {
    if (!visitId || !parentId) return;
    // Guard against double-submit (e.g. rapid clicks or Enter key).
    if (state === "responding") return;

    setState("responding");
    setError(null);

    try {
      const fn = httpsCallable(functions, "respondToVisitInvitation");
      await fn({
        visitId,
        parentId,
        response,
        message: message.trim() || undefined,
        changeRequest: response === "change_requested" ? { reason: message.trim() || "Schedule conflict" } : undefined,
      });
      setResponseType(response);
      setState("success");
    } catch (err: any) {
      console.error("Error responding:", err);
      setError(err.message || "Failed to submit response. Please try again.");
      setState("ready");
    }
  };

  // NOTE: We intentionally do NOT auto-submit a response based on the URL
  // `action` param. Email clients and security scanners prefetch links, which
  // would silently confirm/decline a visit without the parent's intent. The
  // parent must click a button below to respond. (`action` may still be used
  // to pre-highlight the suggested choice in the UI.)

  const parseTimestamp = (timestamp: any): Date | null => {
    if (!timestamp) return null;
    if (timestamp.toDate) return timestamp.toDate();
    if (timestamp._seconds) return new Date(timestamp._seconds * 1000);
    if (typeof timestamp === "string") return new Date(timestamp);
    return null;
  };

  const formatDate = (timestamp: any) => {
    const date = parseTimestamp(timestamp);
    if (!date) return "";
    return date.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  };

  const formatTime = (timestamp: any) => {
    const date = parseTimestamp(timestamp);
    if (!date) return "";
    return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-zinc-900 dark:via-zinc-800 dark:to-zinc-900">
      <div className="w-full max-w-lg px-4">
        <div className="w-full p-8 bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 dark:bg-zinc-900/80 dark:border-zinc-700/50">
          <div className="text-center mb-6">
            <img src={logo} alt="VisitProof" className="w-16 h-16 mx-auto mb-4" />
          </div>

          {/* Loading */}
          {state === "loading" && (
            <div className="text-center py-8">
              <Text className="text-zinc-500 dark:text-zinc-400">Loading visit details...</Text>
            </div>
          )}

          {/* Not found */}
          {state === "not_found" && (
            <div className="text-center py-8">
              <Heading className="text-xl font-bold text-zinc-900 dark:text-white mb-2">
                Visit Not Found
              </Heading>
              <Text className="text-zinc-500 dark:text-zinc-400">
                This invitation link may have expired or the visit may have been canceled.
              </Text>
            </div>
          )}

          {/* Success */}
          {state === "success" && (
            <div className="text-center py-4">
              {responseType === "accepted" ? (
                <>
                  <CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <Heading className="text-xl font-bold text-zinc-900 dark:text-white mb-2">
                    Visit Confirmed
                  </Heading>
                  <Text className="text-zinc-600 dark:text-zinc-400">
                    Thank you for confirming your attendance. You'll receive a reminder before the visit.
                  </Text>
                </>
              ) : responseType === "declined" ? (
                <>
                  <XCircleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
                  <Heading className="text-xl font-bold text-zinc-900 dark:text-white mb-2">
                    Response Received
                  </Heading>
                  <Text className="text-zinc-600 dark:text-zinc-400">
                    Your response has been recorded. The visit coordinator will follow up with you.
                  </Text>
                </>
              ) : (
                <>
                  <CalendarDaysIcon className="w-16 h-16 text-amber-500 mx-auto mb-4" />
                  <Heading className="text-xl font-bold text-zinc-900 dark:text-white mb-2">
                    Change Request Sent
                  </Heading>
                  <Text className="text-zinc-600 dark:text-zinc-400">
                    Your request has been sent to the visit coordinator. They'll reach out to reschedule.
                  </Text>
                </>
              )}
            </div>
          )}

          {/* Responding */}
          {state === "responding" && (
            <div className="text-center py-8">
              <Text className="text-zinc-500 dark:text-zinc-400">Submitting your response...</Text>
            </div>
          )}

          {/* Ready — show visit details and response options */}
          {state === "ready" && visitData && (
            <div>
              <Heading className="text-xl font-bold text-center text-zinc-900 dark:text-white mb-6">
                {action === "decline" ? "Decline or Request Change" : "Confirm Your Visit"}
              </Heading>

              {/* Visit details */}
              <div className="p-4 mb-6 bg-zinc-50 dark:bg-zinc-800 rounded-xl space-y-3">
                <div className="flex items-center gap-3">
                  <CalendarDaysIcon className="w-5 h-5 text-zinc-400" />
                  <Text className="text-zinc-900 dark:text-white font-medium">
                    {formatDate(visitData.startDate)}
                  </Text>
                </div>
                <div className="flex items-center gap-3">
                  <ClockIcon className="w-5 h-5 text-zinc-400" />
                  <Text className="text-zinc-900 dark:text-white">
                    {formatTime(visitData.startDate)} – {formatTime(visitData.endDate)}
                  </Text>
                </div>
                {visitData.location?.name && (
                  <div className="flex items-center gap-3">
                    <MapPinIcon className="w-5 h-5 text-zinc-400" />
                    <Text className="text-zinc-600 dark:text-zinc-400">
                      {visitData.location.formattedAddress || visitData.location.name}
                    </Text>
                  </div>
                )}
              </div>

              {error && (
                <div className="p-3 mb-4 bg-red-50 border border-red-200 rounded-lg dark:bg-red-900/20 dark:border-red-800">
                  <Text className="text-sm text-red-700 dark:text-red-300">{error}</Text>
                </div>
              )}

              {action === "decline" ? (
                <div className="space-y-4">
                  <Field>
                    <Label className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                      Reason or preferred time (optional)
                    </Label>
                    <Textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Let us know why or suggest a better time..."
                      rows={3}
                    />
                  </Field>
                  <div className="flex gap-3">
                    <Button
                      color="red"
                      className="flex-1"
                      onClick={() => handleRespond("declined")}
                    >
                      Decline Visit
                    </Button>
                    <Button
                      className="flex-1"
                      onClick={() => handleRespond("change_requested")}
                    >
                      Request Different Time
                    </Button>
                  </div>
                  <div className="text-center">
                    <Button
                      plain
                      onClick={() => handleRespond("accepted")}
                    >
                      Actually, I can make it — Accept
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <Button
                    color="green"
                    className="w-full py-3"
                    onClick={() => handleRespond("accepted")}
                  >
                    Confirm Attendance
                  </Button>
                  <div className="text-center">
                    <Button
                      plain
                      onClick={() => {
                        // Switch to decline mode
                        window.history.replaceState(null, "", `${window.location.pathname}?parentId=${parentId}&action=decline`);
                        setResponseType(null);
                      }}
                    >
                      I can't make it
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-zinc-500">
          Powered by{" "}
          <a href="https://visitproof.com" className="underline hover:text-zinc-700 dark:hover:text-zinc-300">
            VisitProof
          </a>
        </p>
      </div>
    </div>
  );
};

export default InvitationResponsePage;
